import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, FileCheck, Users, ClipboardCheck, ArrowRight, Command as CmdIcon, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import { ZertivoSpinner } from '@/components/ZertivoLoader';

/**
 * Global Command Palette (⌘K / Strg+K).
 * Mounted once at the app root via App.js — listens for the keyboard
 * shortcut and opens a modal with a debounced search over companies,
 * users, certificates and audits.
 *
 * Each result is keyboard-navigable and Enter takes the user there.
 */

const SECTIONS = [
  { key: 'companies',    endpoint: '/admin/search/companies',    icon: Building2,      label: 'Unternehmen', route: (r) => `/admin/companies?focus=${r.id}` },
  { key: 'certificates', endpoint: '/admin/search/certificates', icon: FileCheck,      label: 'Zertifikate', route: (r) => `/admin/certificates?focus=${r.id}` },
  { key: 'users',        endpoint: '/admin/search/users',        icon: Users,          label: 'Benutzer',    route: (r) => `/admin/users?focus=${r.id}` },
  { key: 'audits',       endpoint: '/admin/search/audits',       icon: ClipboardCheck, label: 'Audits',      route: (r) => `/admin/audits?focus=${r.id}` },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Global shortcut listener
  useEffect(() => {
    const handler = (e) => {
      const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      if (modifier && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Auto-focus & reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Debounced search
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/admin/search', { params: { q, limit: 18 } });
      const flat = [];
      SECTIONS.forEach((s) => {
        (data?.[s.key] || []).forEach((r) => flat.push({ ...r, __section: s }));
      });
      setResults(flat);
      setActiveIdx(0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const go = (r) => {
    navigate(r.__section.route(r));
    setOpen(false);
  };

  // Arrow-key navigation
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(results.length - 1, i + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter' && results[activeIdx]) { e.preventDefault(); go(results[activeIdx]); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh] bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setOpen(false)}
          data-testid="command-palette"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: -12, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -12, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', damping: 22, stiffness: 260 }}
            className="w-full max-w-xl bg-white rounded-2xl shadow-[0_30px_80px_-15px_rgba(0,0,0,0.3)] border border-[#E4E4E7] overflow-hidden"
          >
            {/* Input row */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#F4F4F5]">
              <Search className="w-4 h-4 text-[#A1A1AA] flex-shrink-0" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Suche Unternehmen, Nutzer, Zertifikate, Audits…"
                data-testid="command-palette-input"
                className="flex-1 bg-transparent text-[14px] text-primary outline-none placeholder:text-[#A1A1AA]"
              />
              {loading && <ZertivoSpinner size={16} />}
              <kbd className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold text-[#A1A1AA] bg-[#F4F4F5] border border-[#E4E4E7] rounded px-1.5 py-0.5">ESC</kbd>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {query.trim().length < 2 ? (
                <EmptyHelp />
              ) : results.length === 0 && !loading ? (
                <div className="py-10 text-center text-[13px] text-secondary">
                  Keine Treffer für <span className="font-semibold text-primary">„{query}"</span>
                </div>
              ) : (
                <div className="py-1">
                  {results.map((r, i) => {
                    const Icon = r.__section.icon;
                    return (
                      <button
                        key={`${r.__section.key}-${r.id}-${i}`}
                        onClick={() => go(r)}
                        onMouseEnter={() => setActiveIdx(i)}
                        data-testid={`cmd-result-${r.__section.key}-${r.id}`}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-[#F0FDF4]' : 'hover:bg-[#FAFAFA]'}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${i === activeIdx ? 'bg-[#D1F4E2] text-[#0F8A5F]' : 'bg-[#F4F4F5] text-[#71717A]'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13.5px] font-semibold text-primary truncate">{r.title || r.name || r.email}</p>
                          <p className="text-[11.5px] text-tertiary truncate">
                            <span className="font-semibold uppercase tracking-wider">{r.__section.label}</span>
                            {r.subtitle ? <> · {r.subtitle}</> : null}
                          </p>
                        </div>
                        <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 transition-opacity ${i === activeIdx ? 'opacity-100 text-[#0F8A5F]' : 'opacity-0'}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-[#FAFAFA] border-t border-[#F4F4F5] text-[10.5px] text-[#A1A1AA]">
              <span className="flex items-center gap-2">
                <kbd className="bg-white border border-[#E4E4E7] rounded px-1 py-0.5 font-mono">↑↓</kbd> navigieren
                <kbd className="bg-white border border-[#E4E4E7] rounded px-1 py-0.5 font-mono">Enter</kbd> öffnen
              </span>
              <span className="flex items-center gap-1">
                <CmdIcon className="w-3 h-3" /> Zertivo Search
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function EmptyHelp() {
  const hints = [
    { icon: Building2, label: 'Unternehmen nach Name oder Slug' },
    { icon: FileCheck, label: 'Zertifikate nach Nummer oder Firma' },
    { icon: Users, label: 'Benutzer nach E-Mail oder Name' },
    { icon: ClipboardCheck, label: 'Audits nach ID oder Firma' },
  ];
  return (
    <div className="py-5 px-4 space-y-2">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2 px-1">Tipps</p>
      {hints.map((h, i) => {
        const Icon = h.icon;
        return (
          <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg">
            <Icon className="w-3.5 h-3.5 text-[#A1A1AA]" />
            <span className="text-[12.5px] text-secondary">{h.label}</span>
          </div>
        );
      })}
    </div>
  );
}
